/**
 * TeamWorkspacePage - Full team workspace view (DRR-061)
 *
 * Data-driven: All data from backend getTeamWorkspace + getTeamPresence queries.
 * Layout scaffolding (cards, grids, tabs) is standard UI — not hardcoded data.
 *
 * CONSTRAINTS:
 * - Mantine v8.x only
 * - NO direct useQuery — uses gql + useQuery<Record<string, any>>(codegen pending for new queries)
 * - Multi-tenant via useTenantId()
 * - Backend is single source of truth for all displayed data
 */

import { useState, useMemo } from 'react';
import {
  Container,
  Title,
  Text,
  SimpleGrid,
  Card,
  Group,
  Stack,
  TextInput,
  Tabs,
  Table,
  Badge,
  Skeleton,
  Alert,
} from '@mantine/core';
import {
  IconSearch,
  IconUsers,
  IconAlertCircle,
} from '@tabler/icons-react';

import { useAuth } from '@/auth/AuthContext';
import { useTenantId } from '@/auth';
import { useMyActor } from '@/hooks/useMyActor';
import { useGetTeamWorkspaceQuery } from '@/generated/graphql';
import {
  TeamMemberCard,
  type PresenceStatus,
} from '@ferp/workflow-forms';

// ============================================================================
// Component
// ============================================================================

export function TeamWorkspacePage() {
  const { user } = useAuth();
  const tenantId = useTenantId();
  const [searchQuery, setSearchQuery] = useState('');

  // Step 1: Get current user's actor to find their team (canonical hook)
  const { teamId, loading: myActorLoading } = useMyActor();
  const myActorError = null; // useMyActor doesn't expose error; kept for hasError check

  // Step 2: Get full team workspace data
  const {
    data: workspaceData,
    loading: workspaceLoading,
    error: workspaceError,
  } = useGetTeamWorkspaceQuery({
    variables: { teamId: teamId ?? '' },
    skip: !teamId,
    pollInterval: 60000, // Refresh presence every 60s
  });

  const workspace = workspaceData?.getTeamWorkspace ?? null;

  // Build presence lookup map
  const presenceMap = useMemo(() => {
    const map = new Map<string, PresenceStatus>();
    if (workspace?.presence) {
      for (const p of workspace.presence) {
        map.set(p.actorId, p.status as PresenceStatus);
      }
    }
    return map;
  }, [workspace?.presence]);

  // Filter members by search query (client-side — team sizes < 100)
  const filteredMembers = useMemo(() => {
    if (!workspace?.members) return [];
    if (!searchQuery.trim()) return workspace.members;

    const q = searchQuery.toLowerCase();
    return workspace.members.filter(
      (m) =>
        m.name.toLowerCase().includes(q) ||
        m.actorType.toLowerCase().includes(q)
    );
  }, [workspace?.members, searchQuery]);

  const isLoading = myActorLoading || workspaceLoading;
  const hasError = myActorError || workspaceError;

  // No team assignment
  if (!isLoading && !teamId) {
    return (
      <Container size="xl">
        <Stack gap="lg">
          <Title order={2}>My Team</Title>
          <Alert icon={<IconUsers size={16} />} color="blue" variant="light">
            You are not currently assigned to a team. Contact your administrator to be added to a team.
          </Alert>
        </Stack>
      </Container>
    );
  }

  return (
    <Container size="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between" ta="left">
          <div>
            <Title order={2}>
              {isLoading ? <Skeleton height={28} width={200} /> : workspace?.teamName ?? 'My Team'}
            </Title>
            {workspace?.teamDescription && (
              <Text c="dimmed" size="sm">
                {workspace.teamDescription}
              </Text>
            )}
          </div>
          <Group gap="sm">
            {workspace && (
              <>
                <Badge variant="light" color="blue" size="lg">
                  {workspace.memberCount} members
                </Badge>
                <Badge variant="light" color="green" size="lg">
                  {workspace.onlineCount} online
                </Badge>
              </>
            )}
          </Group>
        </Group>

        {/* Error Alert */}
        {hasError && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Data Loading Error"
            color="red"
            variant="light"
          >
            Some team data could not be loaded. Showing available data.
          </Alert>
        )}

        {/* Search */}
        <TextInput
          placeholder="Search team members..."
          leftSection={<IconSearch size={16} />}
          value={searchQuery}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.currentTarget.value)}
        />

        {/* Member Grid */}
        {isLoading ? (
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
            {Array.from({ length: 6 }).map((_: any, i) => (
              <Skeleton key={i} height={140} radius="md" />
            ))}
          </SimpleGrid>
        ) : (
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
            {filteredMembers.map((member) => (
              <TeamMemberCard
                key={member.id}
                actorId={member.id}
                name={member.name}
                actorType={member.actorType}
                presenceStatus={presenceMap.get(member.id) ?? 'OFFLINE'}
                currentTaskName={member.currentTaskName}
                workload={member.workload}
                maxCapacity={member.maxCapacity}
                successRate={member.successRate}
              />
            ))}
            {filteredMembers.length === 0 && (
              <Text c="dimmed" size="sm">
                {searchQuery ? 'No members match your search.' : 'No team members found.'}
              </Text>
            )}
          </SimpleGrid>
        )}

        {/* Team Queue Status (if available from workspace) */}
        {workspace?.teamType && (
          <Card withBorder>
            <Group justify="space-between">
              <Title order={4}>Team Info</Title>
              <Badge variant="light">{workspace.teamType}</Badge>
            </Group>
          </Card>
        )}
      </Stack>
    </Container>
  );
}
